import type { ListedPiece } from "@/lib/host-panel/catalog-types";

/**
 * The house's ready-made lists ("Listas de Sugestão"): the catalog's `sugestao` context in the
 * design (`Catalogo.dc.html`, `ctx() === 'sugestao'`), one page per list.
 *
 * **The lists themselves are not here.** They are the backend's templates, read by
 * `lib/create-list/suggestion-templates` — by the creation, by the staff screens and, since the
 * API opened the reads without a Bearer (PR #57, 2026-10-07), by the homepage and the public
 * pages too. What is left in this module is what the surface needs regardless of where a list
 * comes from: its address and the two count lines the design writes.
 *
 * Until 2026-10-08 this module also held the design package's own data — three lists and a table
 * of products, 33 KB of pieces in the repository — because the template reads needed a customer
 * token and a visitor has none. That made the same lists come from two sources in one product,
 * and nothing the house registered ever reached the public site. The data is gone with the
 * reason for it.
 */

/** A list as the suggestion hero and its catalog read it, wherever it was read from. */
export type SuggestionList = {
  /** The address of the list's page, `/listas-de-sugestao/<slug>`. */
  slug: string;
  name: string;
  /** The hero's title, over two lines: the lead, then the accent in green (`tituloA`/`tituloB`). */
  titleLead: string;
  titleAccent: string;
  description: string;
  coverUrl: string;
  /** The pieces in the curation's order, each with the units the house suggests. */
  pieces: readonly ListedPiece[];
  /** The hero's count line, as the design writes it ("109 peças · 20 coleções"). */
  countLabel: string;
};

export const SUGGESTION_LISTS_BASE_PATH = "/listas-de-sugestao";

export function suggestionListHref(slug: string): string {
  return `${SUGGESTION_LISTS_BASE_PATH}/${slug}`;
}

/** `ev.contagem` in the design: the pieces, then the collections, each in its own number. */
export function suggestionCountLabel(pieces: readonly ListedPiece[]): string {
  const collections = new Set(pieces.map((piece) => piece.collection).filter((name) => name !== "")).size;

  return `${pieces.length} ${pieces.length === 1 ? "peça" : "peças"} · ${collections} ${collections === 1 ? "coleção" : "coleções"}`;
}

/** The "unidades desejadas" line of a suggestion's card (`qtdLabel`). */
export function desiredUnitsLabel(quantity: number): string {
  return `${quantity} ${quantity === 1 ? "unidade desejada" : "unidades desejadas"}`;
}
